import React, { useEffect } from 'react';
import { Form, Input, Button, Card, message, Upload, Select } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { addShop, updateShop } from '../../store/shopsSlice';
import { formatName, formatMobile } from '../../utils/formatters';
import { API_URL } from '../../utils/api';

const ShopForm = () => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const { shops } = useSelector((state) => state.shops);
    const { user } = useSelector((state) => state.auth);

    const isEditMode = !!id;

    useEffect(() => {
        if (isEditMode) {
            const shop = shops.find((s) => s.id === id);
            if (shop) {
                // Ensure BDE only edits their own shop
                if (shop.bdeId && shop.bdeId !== user.id) {
                    message.error("You don't have permission to edit this shop");
                    navigate('/bde/shops');
                    return;
                }

                // Prepare initial values
                const initialValues = { ...shop };
                if (shop.boardImageUrl) {
                    initialValues.boardImage = [{
                        uid: '-1',
                        name: 'Existing Image',
                        status: 'done',
                        url: shop.boardImageUrl.startsWith('http') ? shop.boardImageUrl : `${API_URL}${shop.boardImageUrl}`,
                    }];
                }
                form.setFieldsValue(initialValues);
            } else {
                message.error('Shop not found');
                navigate('/bde/shops');
            }
        } else if (user) {
            form.setFieldsValue({ onboardedBy: user.name });
        }
    }, [id, isEditMode, shops, form, navigate, user, user.id]);

    const normFile = (e) => {
        if (Array.isArray(e)) {
            return e;
        }
        return e?.fileList;
    };

    const onFinish = (values) => {
        const formData = new FormData();

        // Append standard fields
        formData.append('name', formatName(values.name));
        formData.append('ownerName', formatName(values.ownerName));
        const mobile = formatMobile(values.ownerNumber);
        formData.append('ownerNumber', mobile);
        formData.append('contactNumber', mobile);
        formData.append('pincode', values.pincode);
        if (values.gstNo) formData.append('gstNo', values.gstNo);
        formData.append('shopType', values.shopType || 'General Store');
        formData.append('address', values.address);
        if (values.location) formData.append('location', values.location);
        if (values.onboardedBy) formData.append('onboardedBy', values.onboardedBy);

        // Attach BDE ID to identify creator
        if (!isEditMode) {
            formData.append('bdeId', user.id);
        }

        // Handle Image
        if (values.boardImage && values.boardImage.length > 0) {
            const file = values.boardImage[0].originFileObj;
            if (file) {
                formData.append('boardImage', file);
            }
        }

        if (isEditMode) {
            // Logic: If BDE edits a Rejected shop, does it reset to Pending? 
            // Based on req "he/she will solve that query", implies re-submission.
            // Backend automatically defaults 'Pending' if bdeId is present in body? 
            // Re-reading backend logic: `if (shopData.bdeId) shopData.status = 'Pending'`.
            // So we must append bdeId even on update to trigger Pending status?
            // Or relies on existing document? `findOneAndUpdate` merges.
            // If I append bdeId here, it *might* trigger the logic if I change PUT to use that logic.
            // Wait, standard PUT logic is `{ ...req.body }`. 
            // I should update shopRoutes logic for PUT to handle re-submission or handle it here.
            // Backend PUT logic doesn't explicitly reset status.
            // Frontend should explicitly set status 'Pending' on re-submission? 
            // Or backend logic should be updated?
            // "flow is after bde submit shop data... display new shop after verify".
            // Let's explicitly append status 'Pending' on edit to be safe.
            formData.append('status', 'Pending');

            dispatch(updateShop({ id, data: formData }))
                .unwrap()
                .then(() => {
                    message.success('Shop updated successfully and sent for verification');
                    navigate('/bde/shops');
                })
                .catch((err) => {
                    console.error("Update Shop Error:", err);
                    let msg = 'Failed to update shop';
                    if (err.message === 'Network Error') {
                         msg = 'Network Error: Please check your internet connection or try a smaller image (max 15MB).';
                    } else if (err.response?.data?.message) {
                        msg = err.response.data.message;
                    } else if (err.message) {
                        msg = err.message;
                    }
                    message.error(msg);
                });
        } else {
            formData.append('id', `SHOP-${Date.now()}`);

            dispatch(addShop(formData))
                .unwrap()
                .then(() => {
                    message.success('Shop created successfully and sent for verification');
                    navigate('/bde/shops');
                })
                .catch((err) => {
                    console.error("Create Shop Error:", err);
                    let msg = 'Failed to create shop';
                    if (err.message === 'Network Error') {
                        msg = 'Network Error: Please check your internet connection or try a smaller image (max 15MB).';
                    } else if (err.response?.data?.message) {
                        msg = err.response.data.message;
                    } else if (err.message) {
                        msg = err.message;
                    }
                    message.error(msg);
                });
        }
    };

    return (
        <Card title={isEditMode ? 'Edit Shop' : 'Add New Shop'}>
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
            >
                <Form.Item
                    name="onboardedBy"
                    label="Who onboarded this shop"
                >
                    <Input disabled />
                </Form.Item>

                <Form.Item
                    name="name"
                    label="Shop Name"
                    rules={[{ required: true, message: 'Please input the shop name!' }]}
                >
                    <Input placeholder="Enter shop name" />
                </Form.Item>

                <Form.Item
                    name="ownerName"
                    label="Owner Name"
                    rules={[{ required: true, message: 'Please input the owner name!' }]}
                >
                    <Input placeholder="Enter owner name" />
                </Form.Item>

                <Form.Item
                    name="ownerNumber"
                    label="Owner Number"
                    rules={[{ required: true, message: 'Please input owner number' }]}
                >
                    <Input placeholder="Enter owner number" />
                </Form.Item>

                <Form.Item
                    name="pincode"
                    label="Pincode"
                    rules={[{ required: true, message: 'Please input the pincode!' }]}
                >
                    <Input placeholder="Enter pincode" />
                </Form.Item>

                <Form.Item
                    name="gstNo"
                    label="GST No."
                >
                    <Input placeholder="Enter GST number" />
                </Form.Item>

                <Form.Item
                    name="shopType"
                    label="Shop Type"
                    rules={[{ required: true, message: 'Please select shop type' }]}
                    initialValue="General Store"
                >
                    <Select>
                        <Select.Option value="General Store">General Store</Select.Option>
                        <Select.Option value="Kiran Store">Kiran Store</Select.Option>
                        <Select.Option value="Super Market">Super Market</Select.Option>
                        <Select.Option value="Cloth Shop">Cloth Shop</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    name="address"
                    label="Address"
                    rules={[{ required: true, message: 'Please input the address!' }]}
                >
                    <Input.TextArea rows={4} placeholder="Enter address" />
                </Form.Item>

                <Form.Item
                    name="boardImage"
                    label="Shop Board Image"
                    valuePropName="fileList"
                    getValueFromEvent={normFile}
                    rules={[{ required: true, message: 'Please upload shop board image!' }]}
                >
                    <Upload
                        name="boardImage"
                        listType="picture"
                        maxCount={1}
                        beforeUpload={() => false}
                    >
                        <Button icon={<UploadOutlined />}>Click to Upload</Button>
                    </Upload>
                </Form.Item>

                <Form.Item
                    name="location"
                    label="Shop Location"
                >
                    <Input placeholder="Latitude, Longitude or area" />
                </Form.Item>

                <Form.Item>
                    <Button type="primary" htmlType="submit">
                        {isEditMode ? 'Update & Submit' : 'Create & Submit'}
                    </Button>
                    <Button style={{ marginLeft: 8 }} onClick={() => navigate('/bde/shops')}>
                        Cancel
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
};

export default ShopForm;
